import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Image,
  Heading,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Avatar,
  useDisclosure,
  Divider,
  useToast,
  Textarea,

} from "@chakra-ui/react";
import { useSelector, useDispatch } from "react-redux";
import { FaMapMarkerAlt, FaFileDownload } from "react-icons/fa";
import { MdCurrencyRupee } from "react-icons/md";
import { FaMapLocationDot } from "react-icons/fa6";
import { NavLink, useNavigate } from "react-router-dom";
import Rating from "../../components/rating";
import moment from "moment";
import { cancelBooking, sendMessage } from "../../api-services";
// Import jsPDF
import { jsPDF } from 'jspdf';

const BookingDetails = ({ isOpen, onClose, providerData, bookingStatus }) => {
  const businessDetail = providerData?.businesses;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { redDark, darkYellow, bodyTextColor } = useSelector((state) => state.theme);
  const navigate = useNavigate();
  const { isOpen: isBillOpen, onOpen: openBill, onClose: closeBill } = useDisclosure();
  const { isOpen: isCancelOpen, onOpen: openCancel, onClose: closeCancel } = useDisclosure();
  const [cancelReason, setCancelReason] = useState("User Cancelled");
  const [customReason, setCustomReason] = useState("");
  const billRef = useRef();
  const toast = useToast();

  const formatAddress = (address) => {
    if (!address) return "Address not available";
    const { address_line_one, address_line_two, city, state, pincode } = address;

    return (
      <>
        {address_line_one}, {address_line_two}, {city}, {state} - {pincode}.
      </>
    );
  };

  const onChat = (user) => {
    const newMessage = {
      booking_id: user?.otp?.booking_id,
      receiver_id: user?.business_id,
      message: `Hello , ${user?.business_name}`,
      chat_id: user?.chatID,
    };
    const chatMessage = async (newMessage) => {
      try {
        setLoading(true);
        const data = await sendMessage(newMessage);
        console.log("initial message chat", data);
        navigate(`/chat`);
      } catch (error) {
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    };
    chatMessage(newMessage);
  };

  const handleCancelBooking = async () => {
    // Get the final reason - either from predefined options or custom input
    const finalReason = cancelReason === "Other" ? customReason : cancelReason;

    if (!finalReason || finalReason.trim() === "") {
      toast({
        title: "Error",
        description: "Please provide a reason for cancellation",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      booking_id: providerData?.id,
      reason: finalReason,
    };

    toast({
      title: "Processing",
      description: "Cancelling your booking...",
      status: "info",
      duration: 2000,
      isClosable: true,
    });

    try {
      const res = await cancelBooking(payload);
      console.log(res);

      if (res?.success) {
        toast({
          title: "Success",
          description: res?.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        // Close the cancel reason modal
        closeCancel();
        // Close the main booking details modal
        onClose();
      } else {
        toast({
          title: "Error",
          description: res?.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel booking. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Simplified PDF export using just jsPDF without autotable
  const exportPDF = () => {
    try {
      toast({
        title: "Processing",
        description: "Generating your PDF...",
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      const doc = new jsPDF();

      doc.setFont("times", "normal"); // still using times font

      doc.setFontSize(22);
      doc.setTextColor(41, 128, 185);
      doc.text("TRIDER", 105, 15, { align: 'center' });

      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("Booking Invoice", 105, 25, { align: 'center' });

      doc.setDrawColor(200, 200, 200);
      doc.line(20, 30, 190, 30);

      doc.setFontSize(14);
      doc.setFont('times', 'bold');
      doc.text("Booking Details", 20, 40);

      doc.setFontSize(10);
      doc.setFont('times', 'normal');

      const leftColumnX = 20;
      const rightColumnX = 140;
      const startY = 50;

      doc.text(`Booking ID: ${providerData?.order_no || 'N/A'}`, leftColumnX, startY);
      doc.text(`Service: ${providerData?.booking_services?.[0]?.service_name || 'Service'}`, leftColumnX, startY + 7);

      const address = formatAddress(providerData?.booking_address).props?.children || "Address not available";
      const addressText = `Location: ${typeof address === 'string' ? address : address.join('')}`;
      const splitAddress = doc.splitTextToSize(addressText, 80);
      doc.text(splitAddress, leftColumnX, startY + 14);

      doc.text(`Date: ${providerData?.booking_date || 'N/A'}`, rightColumnX, startY);
      doc.text(`Time: ${moment(providerData?.booking_time, "HH:mm:ss").format("h:mm A") || 'N/A'}`, rightColumnX, startY + 7);

      const durationType = providerData?.booking_services?.[0]?.days > 0 ? "Days" : "Hours";
      const durationValue = providerData?.booking_services?.[0]?.days > 0
        ? providerData?.booking_services?.[0]?.days
        : providerData?.booking_services?.[0]?.hours || 0;
      doc.text(`Duration: ${durationValue} ${durationType}`, rightColumnX, startY + 14);

      const addressHeight = splitAddress.length * 5;
      const lineY = Math.max(startY + addressHeight + 10, startY + 30);

      doc.line(20, lineY, 190, lineY);

      doc.setFontSize(14);
      doc.setFont('times', 'bold');
      doc.text("Payment Details", 20, lineY + 10);

      doc.setFontSize(10);
      doc.setFont('times', 'normal');

      const leftColumn = 20;
      const rightColumn = 140;
      let rowY = lineY + 20;

      doc.text("Total Amount:", leftColumn, rowY);
      doc.text("Payment Gateway Fees:", leftColumn, rowY + 7);
      doc.text("Platform Fee:", leftColumn, rowY + 14);
      doc.text("Service Tax:", leftColumn, rowY + 21);
      doc.text("CGST:", leftColumn, rowY + 28);
      doc.text("SGST:", leftColumn, rowY + 35);

      doc.line(leftColumn, rowY + 42, rightColumn + 30, rowY + 42);

      doc.setFont('times', 'bold');
      doc.text("Grand Total:", leftColumn, rowY + 49);

      doc.setFont('times', 'normal');

      // ✅ Corrected formatAmount function
      const formatAmount = (amount) => {
        return `Rs ${Number(amount || 0).toFixed(2)}`;
      };

      doc.text(formatAmount(providerData?.total_amount), rightColumn, rowY);
      doc.text(formatAmount(0), rightColumn, rowY + 7);
      doc.text(formatAmount(0), rightColumn, rowY + 14);
      doc.text(formatAmount(providerData?.tax), rightColumn, rowY + 21);
      doc.text(formatAmount(providerData?.cgst), rightColumn, rowY + 28);
      doc.text(formatAmount(providerData?.sgst), rightColumn, rowY + 35);

      doc.setFont('times', 'bold');
      doc.text(formatAmount(providerData?.grand_amount), rightColumn, rowY + 49);

      doc.setFontSize(8);
      doc.setFont('times', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text("Thank you for choosing Trider! For support, contact our customer service.", 105, 270, { align: 'center' });
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, 275, { align: 'center' });

      doc.save(`Trider-Invoice-${providerData?.order_no || 'Bill'}.pdf`);

      toast({
        title: "Success",
        description: "PDF has been downloaded",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("PDF generation error:", error);
      toast({
        title: "Error",
        description: "Failed to generate PDF",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };


  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Business Info</ModalHeader>
          <ModalCloseButton />
          <Flex px="4" justifyContent="space-between" width="100%" mb="2">
            <Button
              leftIcon={<FaFileDownload />}
              colorScheme="blue"
              onClick={openBill}
              size="md"
              borderRadius="md"
              shadow="sm"
            >
              View Bill
            </Button>
            {bookingStatus === "cancel" ? (
              <Text color="red.500" fontWeight="bold">Cancelled</Text>
            ) : bookingStatus === "completed" ? (
              <Text color="green.500" fontWeight="bold">Completed</Text>
            ) : (
              <Button
                colorScheme="red"
                onClick={openCancel}
                size="md"
                borderRadius="md"
                shadow="sm"
              >
                Cancel Booking
              </Button>
            )}
          </Flex>
          <ModalBody>
            <Box>
              <Image
                src={providerData?.business_image}
                m="0 auto"
                borderRadius="md"
                boxShadow="sm"
              />
              <Box>
                <Box textAlign="center">
                  <Heading as="h6" fontSize="20px" textAlign="center" mt="10px">
                    {providerData?.business_name}
                  </Heading>
                  <Flex justifyContent="center">
                    <Rating
                      total_rating={providerData?.total_rating}
                      reviewsCount={providerData?.total_review}
                    />
                  </Flex>
                  <Text
                    color="#505002"
                    fontSize="18px"
                    fontWeight="600"
                    my="10px"
                    textDecor="underline"
                    as={NavLink}
                    to={`/provider-details?id=${providerData?.business_id}&booking_id=${providerData?.booking_services?.[0]?.booking_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Review
                  </Text>
                </Box>
              </Box>
              <Flex
                justifyContent="space-between"
                pb="5px"
                alignItems="center"
                mb="10px"
              >
                <Flex alignItems="center" gap="5px">
                  <FaMapMarkerAlt size="22" />
                  <Text>{formatAddress(providerData?.booking_address)}</Text>
                </Flex>
                <FaMapLocationDot size="22" />
              </Flex>
              <Box mb="20px" p="5px" bg="#0000000d" borderRadius="md">
                <Flex justifyContent="space-between" pb="5px">
                  <Text fontWeight="600">Booking Id</Text>
                  <Text color="#a6a6a6">{providerData?.order_no}</Text>
                </Flex>
                <Flex justifyContent="space-between" pb="5px">
                  <Text fontWeight="600">Date</Text>
                  <Text color="#a6a6a6">
                    {providerData?.booking_date}{" "}
                    {moment(providerData?.booking_time, "HH:mm:ss").format(
                      "h:mm A"
                    )}
                  </Text>
                </Flex>
                <Flex justifyContent="space-between" pb="5px">
                  <Text fontWeight="600">Payment Mode</Text>
                  <Text color="#a6a6a6">{providerData?.payment_mode === 'online' ? "Online" : "Cash On"}</Text>
                </Flex>
              </Box>
              <Box>
                <Text fontWeight="700" pb="5px" color={darkYellow}>
                  Service
                </Text>
                <Box mb="20px" p="5px" bg="#0000000d" borderRadius="md">
                  <Flex justifyContent="space-between" pb="5px">
                    <Text fontWeight="600">
                      {providerData?.booking_services?.[0]?.service_name || "Service Not Available"}
                    </Text>
                    <Text fontWeight="600">
                      Qty : {providerData?.booking_services?.[0]?.qty || 0}
                    </Text>
                  </Flex>
                  <Flex justifyContent="space-between" pb="5px">
                    {providerData?.booking_services?.[0]?.days > 0 ? (
                      <Text fontWeight="600" fontSize="12px">
                        No. of days : {providerData?.booking_services[0]?.days}
                      </Text>
                    ) : (
                      <Text fontWeight="600" fontSize="12px">
                        No. of hours : {providerData?.booking_services?.[0]?.hours || 0}
                      </Text>
                    )}

                    <Flex alignItems="center" fontWeight="600" fontSize="12px">
                      <MdCurrencyRupee />
                      Per Hour : {providerData?.booking_services?.[0]?.amount || 0}
                    </Flex>
                  </Flex>
                </Box>
              </Box>

              <Box>
                <Text fontWeight="700" pb="5px" color={darkYellow}>
                  Amount
                </Text>
              </Box>
              <Box mb="20px" p="5px" bg="#0000000d" borderRadius="md">
                <Flex justifyContent="space-between" pb="5px">
                  <Text fontWeight="600">Total Amount</Text>
                  <Text fontWeight="600">Rs {providerData?.total_amount || 0}</Text>
                </Flex>
                <Flex justifyContent="space-between" pb="5px" color="red">

                  <Flex alignItems="center" fontWeight="600" color={redDark}>
                    TAX : <MdCurrencyRupee />
                    {providerData?.tax || 0}
                  </Flex>
                  <Flex alignItems="center" fontWeight="600" color={redDark}>
                    CGST : {providerData?.cgst || 0}
                  </Flex>
                  <Flex alignItems="center" fontWeight="600" color={redDark}>
                    SGST : {providerData?.sgst || 0}
                  </Flex>
                </Flex>
              </Box>

              <Flex
                justifyContent="space-between"
                borderBottom="1px solid #e99c0c42"
                pb="5px"
                mb="10px"
              >
                <Text fontWeight="600" fontSize="20px">
                  Grand Total
                </Text>
                <Flex alignItems="center" fontWeight="600" fontSize="20px">
                  <MdCurrencyRupee />
                  Rs {providerData?.grand_amount || 0}
                </Flex>
              </Flex>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Box width="100%">
              <Flex mb="8px" fontWeight="600">
                Squad Details
              </Flex>
              {businessDetail?.map((businessDetail, index) => (
                <Flex
                  key={index}
                  justifyContent="space-between"
                  width="100%"
                  alignItems="center"
                  py="10px"
                >
                  <Box flex="1" display="flex" gap="8px" alignItems="center">
                    <Avatar
                      src={businessDetail.business_image}
                      borderColor="red"
                    />
                    <Text fontWeight="bold" color={bodyTextColor}>
                      {businessDetail?.business_name}
                    </Text>
                  </Box>
                  <Button
                    colorScheme="green"
                    onClick={() => onChat(businessDetail)}
                  >
                    Chat now
                  </Button>
                </Flex>
              ))}
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Cancellation Reason Modal */}
      <Modal isOpen={isCancelOpen} onClose={closeCancel} >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cancellation Reason</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb="4">Please tell us why you're cancelling this booking(Cancellation Fees 15% will charged):</Text>




            <Textarea
              placeholder="Type your reason here..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
            />


          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeCancel}>
              Go Back
            </Button>
            <Button colorScheme="red" onClick={handleCancelBooking}>
              Confirm Cancellation
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isBillOpen} onClose={closeBill} scrollBehavior="inside" size="3xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader position="sticky" top="0" bg="white" zIndex="1" borderBottomWidth="1px">Booking Bill</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb={6}>
            {providerData ? (
              <Box ref={billRef} p="5" border="1px solid #e2e8f0" borderRadius="md" boxShadow="md">
                {/* Professional Bill Header */}
                <Flex justifyContent="space-between" alignItems="center" mb="4">
                  <Box>
                    <Heading size="md" color={darkYellow || "blue.600"}>INVOICE</Heading>
                    <Text fontSize="sm" color="gray.500">Receipt #{providerData?.order_no}</Text>
                  </Box>
                  {providerData?.business_image && (
                    <Image
                      src={providerData.business_image}
                      alt="Company Logo"
                      maxH="60px"
                      objectFit="contain"
                    />
                  )}
                </Flex>

                <Divider mb="4" />

                {/* Bill Info */}
                <Flex justifyContent="space-between" mb="4" flexDirection={{ base: "column", md: "row" }} gap={{ base: 3, md: 0 }}>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color="gray.600">BILLED TO</Text>
                    <Text fontWeight="medium">{providerData?.business_name || "Customer"}</Text>
                    <Text fontSize="sm">{formatAddress(providerData?.booking_address)}</Text>
                  </Box>
                  <Box textAlign={{ base: "left", md: "right" }}>
                    <Text fontWeight="bold" fontSize="sm" color="gray.600">INVOICE INFO</Text>
                    <Text fontSize="sm"><strong>Date:</strong> {providerData?.booking_date}</Text>
                    <Text fontSize="sm"><strong>Time:</strong> {moment(providerData?.booking_time, "HH:mm:ss").format("h:mm A")}</Text>
                    <Text fontSize="sm"><strong>Payment:</strong> {providerData?.payment_mode === 'online' ? "Online Payment" : "Cash on Delivery"}</Text>
                  </Box>
                </Flex>

                <Box bg="gray.50" p="3" borderRadius="md" mb="4" overflowX="auto">
                  <Flex fontWeight="bold" borderBottom="1px solid" borderColor="gray.200" pb="2" mb="2" minW="600px">
                    <Box flex="3"><Text>Service</Text></Box>
                    <Box flex="1" textAlign="center"><Text>Qty</Text></Box>
                    <Box flex="1" textAlign="center">
                      {providerData?.booking_services?.[0]?.days > 0 ? "Days" : "Hours"}
                    </Box>
                    <Box flex="1" textAlign="right"><Text>Rate</Text></Box>
                    <Box flex="1" textAlign="right"><Text>Amount</Text></Box>
                  </Flex>

                  <Flex py="2" fontSize="sm" minW="600px">
                    <Box flex="3">
                      <Text>{providerData?.booking_services?.[0]?.service_name || "Service"}</Text>
                    </Box>
                    <Box flex="1" textAlign="center">
                      <Text>{providerData?.booking_services?.[0]?.qty || 0}</Text>
                    </Box>
                    <Box flex="1" textAlign="center">
                      <Text>
                        {providerData?.booking_services?.[0]?.days > 0
                          ? providerData?.booking_services?.[0]?.days
                          : providerData?.booking_services?.[0]?.hours || 0}
                      </Text>
                    </Box>
                    <Box flex="1" textAlign="right">
                      <Text>₹{providerData?.booking_services?.[0]?.amount || 0}</Text>
                    </Box>
                    <Box flex="1" textAlign="right">
                      <Text>₹{providerData?.total_amount || 0}</Text>
                    </Box>
                  </Flex>
                </Box>

                {/* Bill Summary */}
                <Flex justifyContent="flex-end" mb="2">
                  <Box width={{ base: "full", md: "250px" }}>
                    <Flex justifyContent="space-between" mb="1">
                      <Text fontSize="sm">Subtotal:</Text>
                      <Text fontSize="sm">₹{providerData?.total_amount || 0}</Text>
                    </Flex>
                    <Flex flexDirection={{ base: "column", md: "row" }} justifyContent="space-between" mb="1">
                      <Text fontSize="sm">TAX: ₹{providerData?.tax || 0}</Text>
                      <Text fontSize="sm">CGST: {providerData?.cgst || 0}</Text>
                      <Text fontSize="sm">SGST: {providerData?.sgst || 0}</Text>
                    </Flex>
                    <Divider my="2" />
                    <Flex justifyContent="space-between" fontWeight="bold">
                      <Text>TOTAL:</Text>
                      <Text>₹{providerData?.grand_amount || 0}</Text>
                    </Flex>
                  </Box>
                </Flex>

                <Divider my="4" />

                {/* Thank You Note */}
                <Text textAlign="center" color="gray.600" fontSize="sm" mt="4">
                  Thank you for your business!
                </Text>
              </Box>
            ) : (
              <Text>Loading Bill Details...</Text>
            )}
          </ModalBody>

          <ModalFooter position="sticky" bottom="0" bg="white" zIndex="1" borderTopWidth="1px">
            <Button
              colorScheme="blue"
              leftIcon={<FaFileDownload />}
              onClick={exportPDF}
            >
              Download PDF
            </Button>
            <Button ml={3} variant="ghost" onClick={closeBill}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export { BookingDetails };